import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import slugify from 'slugify';
import Layout from '~components/Layout';
import Topper from '~components/base/Topper';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import ProgressBar from '../components/custom/ProgressBar';
import SeoSocial from '~components/base/SeoSocial';
import useBusiness from '~lib/useBusiness';
import GlobalStyles from '../styles/GlobalStyles';

import audioContent from '../../content/audio.json';
import videoContent from '../../content/video.json';

export const pageQuery = graphql`
    query {
        site {
          pathPrefix
          siteMetadata {
            title
            sectionName
            projectName
          }
        }
        story: arcStory(storyId: {eq: "UQ77M4WJTNDQNBSTGWHYMAJ3E4"}) {
          headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                name
                url
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              srcSet
              caption
              creditLine
              subtype
              source {
                name
              }
              images {
                caption
                _id
                localImage {
                  childImageSharp {
                    gatsbyImageData(
                      width: 900
                      placeholder: BLURRED
                    )
                  }
                }
                additional_properties {
                  galleryOrder
                }
              }
              localImage {
                childImageSharp {
                  wide: gatsbyImageData(
                    width: 1400
                    placeholder: BLURRED
                  )
                  inline: gatsbyImageData(
                    width: 700
                    placeholder: BLURRED
                  )
                  side: gatsbyImageData(
                    width: 800
                    placeholder: BLURRED
                  )
                }
              }
              # linkData {
              #   image
              #   description
              #   site
              #   title
              #   localImage {
              #     childImageSharp {
              #       gatsbyImageData(width: 660)
              #     }
              #   }
              # }
              bodyElements {
                _id
                content
              }
              citation {
                content
              }
              content_elements {
                url
                type
                caption
                _id
              }
              description {
                basic
              }
            }
            # getting seo social data
            promoImage {
              url
              srcSet
              caption
              credit {
                name
              }
            }
            description {
              basic
            }
            # getting teasers information
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const ArticleTemplate = ({
  data: {
    story,
    site: {
      pathPrefix,
      siteMetadata: { sectionName, projectName },
    },
  },
}) => {
  const slugifiedSection = slugify(sectionName, { lower: true });
  const authorNames = get(story, 'credits', [])
    .map((credit) => credit.name)
    .join(', ');

  useBusiness({
    pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
    environment: process.env.NODE_ENV,
    meterContent: (process.env.NODE_ENV !== 'development'),
    pageId: `apps.${story.slug}`,
    section: slugifiedSection,
    author: authorNames,
    projectName,
  });

  const subhed = get(story, 'subheadlines.basic')
    ? get(story, 'subheadlines.basic')
    : (null);
  const metaUrl = `https://apps.bostonglobe.com${pathPrefix}`;

  // testing for presence of publishDate archie element
  // if it exists, set the pubDateObj to it's value and pass to the Byline component below
  // if it doesn't pass null
  let pubDateObj = null;
  story.bodyElements.forEach((e) => {
    if (e.archieJson) {
      const parsedArchie = JSON.parse(e.archieJson);
      if (parsedArchie.pubDate !== undefined) {
        pubDateObj = parsedArchie;
      }
    }
  });

  const [audio, enableAudio] = useState(false);
  const [audioData, updateAudioData] = useState(audioContent);
  const [videoData, updateVideoData] = useState(videoContent);
  const [stickyView, setStickyView] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);
  const [playing, setPlaying] = useState('');

  useEffect(() => {
    function updateWindowDimensions() {
      const newWidth = document.body.clientWidth;

      if (newWidth !== windowWidth) {
        setWindowWidth(document.body.clientWidth);
        setWindowHeight(window.innerHeight);
      }
    }

    setWindowWidth(document.body.clientWidth);
    setWindowHeight(window.innerHeight);

    window.addEventListener('resize', updateWindowDimensions);

    return (_) => {
      window.removeEventListener('resize', updateWindowDimensions);
    };
  }, []);

  const showAudioDisclaimer = useRef(false);

  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (!isIOS) {
      showAudioDisclaimer.current = true;
    }
  }, []);

  return (
    <Layout>
      <ProgressBar audioData={audioData} stickyView={stickyView} />
      <SeoSocial
        url={metaUrl}
        description={get(story, 'description.basic')}
        title={get(story, 'headlines.basic')}
        imageUrl={get(story, 'promoImage.sizes._1024')}
      />
      <GlobalStyles />
      <Topper
        setStickyView={setStickyView}
        img={story.promoImage}
        head={story.headlines.basic}
        deck={subhed}
        byline={story.credits}
        pubDate={pubDateObj}
      />
      <main id="story__main">
        {story.bodyElements.map((element) => {
          if (element.archieJson) {
            return (
              <ArchieElement
                data={element}
                key={element._id}
                relatedLinks={get(story, 'relatedLinks')}
                enableAudio={enableAudio}
                audio={audio}
                audioData={audioData}
                updateAudioData={updateAudioData}
                videoData={videoData}
                updateVideoData={updateVideoData}
                windowWidth={windowWidth}
                windowHeight={windowHeight}
                playing={playing}
                setPlaying={setPlaying}
                showAudioDisclaimer={showAudioDisclaimer}
              />
            );
          }
          return (
            <BodyElement
              data={element}
              key={element._id}
            />
          );
        })}
      </main>
    </Layout>
  );
};

ArticleTemplate.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
        }),
      ),
      promoImage: PropTypes.shape(),
      headlines: PropTypes.shape({
        basic: PropTypes.string,
      }),
      credits: PropTypes.arrayOf(
        PropTypes.shape(),
      ),
      slug: PropTypes.string,
    }),
    site: PropTypes.shape({
      pathPrefix: PropTypes.string.isRequired,
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default ArticleTemplate;
